import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import QRCode from 'react-qr-code';

const ParkingLots = ({ owner, ownerId }) => {
  const [parkingLots, setParkingLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newLot, setNewLot] = useState({
    name: '',
    address: '',
    spots: '',
    description: ''
  });
  const [creating, setCreating] = useState(false);
  const [selectedLot, setSelectedLot] = useState(null);

  useEffect(() => {
    if (ownerId) {
      fetchParkingLots();
    }
  }, [ownerId]);

  const fetchParkingLots = async () => {
    try {
      const q = query(collection(db, 'parking-lots'), where('ownerId', '==', ownerId));
      const querySnapshot = await getDocs(q);
      const lots = [];
      querySnapshot.forEach((doc) => {
        lots.push({ id: doc.id, ...doc.data() });
      });
      setParkingLots(lots);
    } catch (error) {
      console.error('Error fetching parking lots:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateToken = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let token = '';
    for (let i = 0; i < 12; i++) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
  };

  const handleCreateLot = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const token = generateToken();
      const lotData = {
        ...newLot,
        ownerId,
        ownerName: owner?.name || '',
        ownerEmail: owner?.email || '',
        token,
        createdAt: new Date().toISOString(),
        status: 'active',
        spots: parseInt(newLot.spots) || 0
      };

      await addDoc(collection(db, 'parking-lots'), lotData);
      
      setNewLot({ name: '', address: '', spots: '', description: '' });
      setShowCreateForm(false);
      fetchParkingLots();
    } catch (error) {
      console.error('Error creating parking lot:', error);
      alert('Failed to create parking lot');
    } finally {
      setCreating(false);
    }
  };

  const deleteLot = async (lotId) => {
    if (window.confirm('Are you sure you want to delete this parking lot?')) {
      try {
        await deleteDoc(doc(db, 'parking-lots', lotId));
        fetchParkingLots();
      } catch (error) {
        console.error('Error deleting parking lot:', error);
        alert('Failed to delete parking lot');
      }
    }
  };

  const toggleLotStatus = async (lotId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      await updateDoc(doc(db, 'parking-lots', lotId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      fetchParkingLots();
    } catch (error) {
      console.error('Error updating lot status:', error);
      alert('Failed to update status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getLotUrl = (token) => {
    return `${window.location.origin}/park/${token}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">My Parking Lots</h2>
            <p className="mt-1 text-sm text-gray-600">Manage your parking lot locations and access links</p>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Parking Lot
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Create New Parking Lot</h3>
          <form onSubmit={handleCreateLot} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parking Lot Name
                </label>
                <input
                  type="text"
                  required
                  value={newLot.name}
                  onChange={(e) => setNewLot({ ...newLot, name: e.target.value })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="Main Street Parking"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Number of Spots
                </label>
                <input
                  type="number"
                  required
                  value={newLot.spots}
                  onChange={(e) => setNewLot({ ...newLot, spots: e.target.value })}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="50"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                required
                value={newLot.address}
                onChange={(e) => setNewLot({ ...newLot, address: e.target.value })}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="123 Main St, City, State 12345"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={newLot.description}
                onChange={(e) => setNewLot({ ...newLot, description: e.target.value })}
                rows={3}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="Near shopping center, covered parking available..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Parking Lot'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Parking Lots Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {parkingLots.length === 0 ? (
          <div className="col-span-2 bg-white rounded-lg shadow p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No parking lots</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new parking lot.</p>
          </div>
        ) : (
          parkingLots.map((lot) => (
            <div key={lot.id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{lot.name}</h3>
                    <p className="text-sm text-gray-500">{lot.address}</p>
                  </div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    lot.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {lot.status}
                  </span>
                </div>

                {lot.description && (
                  <p className="text-sm text-gray-600 mb-4">{lot.description}</p>
                )}

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500">Parking Spots</p>
                    <p className="text-lg font-semibold">{lot.spots || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Created</p>
                    <p className="text-sm font-medium">
                      {new Date(lot.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Parking Link</p>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        readOnly
                        value={getLotUrl(lot.token)}
                        className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded bg-gray-50"
                      />
                      <button
                        onClick={() => copyToClipboard(getLotUrl(lot.token))}
                        className="p-1 text-indigo-600 hover:text-indigo-700"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => setSelectedLot(lot)}
                      className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      View QR Code
                    </button>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleLotStatus(lot.id, lot.status)}
                        className={`text-sm font-medium ${
                          lot.status === 'active' 
                            ? 'text-yellow-600 hover:text-yellow-700' 
                            : 'text-green-600 hover:text-green-700'
                        }`}
                      >
                        {lot.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => deleteLot(lot.id)}
                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* QR Code Modal */}
      {selectedLot && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold">{selectedLot.name} QR Code</h3>
              <button
                onClick={() => setSelectedLot(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex justify-center bg-white p-4 border-2 border-gray-200 rounded-lg" id="qr-code-container">
              <QRCode value={getLotUrl(selectedLot.token)} size={200} level="H" id="qr-code" />
            </div>
            <p className="mt-4 text-xs text-gray-500 text-center">
              Print this QR code for customers to scan at your parking lot
            </p>
            
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>QR Code - ${selectedLot.name}</title>
                        <style>
                          body { 
                            display: flex; 
                            flex-direction: column;
                            align-items: center; 
                            justify-content: center; 
                            min-height: 100vh;
                            margin: 0;
                            font-family: system-ui, -apple-system, sans-serif;
                          }
                          h2 { margin-bottom: 20px; }
                          #qr-container { 
                            padding: 20px; 
                            border: 2px solid #e5e7eb;
                            border-radius: 8px;
                          }
                          p { 
                            margin-top: 20px; 
                            color: #6b7280;
                            text-align: center;
                          }
                        </style>
                      </head>
                      <body>
                        <h2>${selectedLot.name}</h2>
                        <div id="qr-container">
                          ${document.getElementById('qr-code').outerHTML}
                        </div>
                        <p>${selectedLot.address}</p>
                        <p style="margin-top: 10px; font-size: 14px;">${getLotUrl(selectedLot.token)}</p>
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.focus();
                  setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                  }, 250);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center justify-center"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2z" />
                </svg>
                Print
              </button>
              
              <button
                onClick={() => {
                  // Create a canvas to draw the QR code
                  const svg = document.getElementById('qr-code');
                  const canvas = document.createElement('canvas');
                  const ctx = canvas.getContext('2d');
                  canvas.width = 300;
                  canvas.height = 350;
                  
                  // White background
                  ctx.fillStyle = 'white';
                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                  
                  // Title
                  ctx.fillStyle = 'black';
                  ctx.font = 'bold 16px Arial';
                  ctx.textAlign = 'center';
                  ctx.fillText(selectedLot.name, canvas.width / 2, 30);
                  
                  // Convert SVG to image
                  const svgData = new XMLSerializer().serializeToString(svg);
                  const img = new Image();
                  img.onload = function() {
                    ctx.drawImage(img, 50, 50, 200, 200);
                    
                    // Address
                    ctx.font = '12px Arial';
                    ctx.fillText(selectedLot.address, canvas.width / 2, 270);
                    
                    // URL
                    ctx.font = '10px Arial';
                    ctx.fillStyle = '#6b7280';
                    ctx.fillText(getLotUrl(selectedLot.token), canvas.width / 2, 290);
                    
                    // Download
                    canvas.toBlob(function(blob) {
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `qr-code-${selectedLot.name.replace(/\s+/g, '-').toLowerCase()}.png`;
                      a.click();
                      URL.revokeObjectURL(url);
                    });
                  };
                  img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
                }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center justify-center"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            </div>
            
            <button
              onClick={() => setSelectedLot(null)}
              className="mt-2 w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParkingLots;